// CMS true-preview entry (docs/10 §2.4). Renders a manifest into a container
// element using the exact playback engine the kiosk player runs — so what an
// editor sees is what a screen shows. The impure services are injected: by
// default media is loaded by plain URL (live fetch, no Cache API) and telemetry
// is dropped. The player passes its own Cache-backed MediaSource + batching
// logger instead (see apps/player).

import { PlaybackEngine } from './playback.ts';
import type { Manifest } from './types.ts';
import type { MediaSource, PlayerLogger } from './ports.ts';
import { NoopLogger, PlainUrlMediaSource } from './ports.ts';

export interface MountPreviewOptions {
  /** How media URLs resolve to playable src. Default: pass-through plain URLs. */
  media?: MediaSource;
  /** Where structured events go. Default: dropped. */
  logger?: PlayerLogger;
  /** Start the RAF playback loop immediately. Default: true. */
  autoplay?: boolean;
}

/** Live control surface for a mounted preview. */
export interface PreviewHandle {
  /** Swap in a new manifest atomically (e.g. as the editor edits). */
  setManifest(manifest: Manifest): void;
  /** The id of the item currently on screen, or null (idle / not yet started). */
  getCurrentItemId(): string | null;
  /** Stop the playback loop. Leaves the last frame on screen. */
  stop(): void;
}

/**
 * Mount a live playback preview of `manifest` into `el`. The engine appends its
 * own stage (two crossfading layers + overlay) as children of `el`; the caller
 * owns `el`'s size/position. Requires the player-core stylesheet to be loaded
 * (`import '@seencast/player-core/styles.css'`).
 */
export function mountPreview(
  el: HTMLElement,
  manifest: Manifest,
  opts: MountPreviewOptions = {},
): PreviewHandle {
  const media = opts.media ?? new PlainUrlMediaSource();
  const logger = opts.logger ?? new NoopLogger();

  // `el` is the HOST: the stage fills it absolutely, so give it a containing
  // block (respect an existing one) and clip the ticker/logo to the box.
  const pos = getComputedStyle(el).position;
  if (!pos || pos === 'static') el.style.position = 'relative';
  el.style.overflow = 'hidden';
  const stageEl = document.createElement('div');
  el.appendChild(stageEl);

  const engine = new PlaybackEngine(stageEl, media, logger);
  engine.setManifest(manifest);
  if (opts.autoplay !== false) engine.start();
  return {
    setManifest: (m) => engine.setManifest(m),
    getCurrentItemId: () => engine.getCurrentItemId(),
    stop: () => engine.stop(),
  };
}
